import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { ScrollView } from "react-native";
import { useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { useAuth } from "@src/features/tenants";
import { FEATURE_FLAGS, useFeatureFlags } from "@src/services/feature-flags/feature-flags";
import { useSnapshotHistory, useSignedUrl, type SnapshotHistoryItem } from "@visioni/a3s-media/app";
import { toISODateString } from "@visioni/a3s-codecs";
import { SiteId, AssetCameraKey } from "@visioni/a3s-schema";

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-AU", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatDateRange(startIso: string, endIso: string): string {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const fmt = (d: Date) => d.toLocaleString("en-AU", { month: "short", day: "numeric" });
  return `${fmt(s)} – ${fmt(e)}`;
}

function formatTimeDiff(fromIso: string, toIso: string): string {
  const diffMs = Math.abs(new Date(fromIso).getTime() - new Date(toIso).getTime());
  const hours = Math.round(diffMs / (60 * 60 * 1000));
  if (hours < 24) return `~${String(hours)}h prior`;
  const days = Math.round(hours / 24);
  if (days === 1) return "~1d prior";
  return `~${String(days)}d prior`;
}

function findClosestTo24hBefore(snapshots: SnapshotHistoryItem[], fromIndex: number): number {
  const fromTime = new Date(snapshots[fromIndex]!.timestamp).getTime();
  const target = fromTime - 24 * 60 * 60 * 1000;
  let bestIdx = snapshots.length - 1;
  let bestDiff = Infinity;
  // Sorted newest-first — only consider older snapshots (higher indices)
  for (let i = fromIndex + 1; i < snapshots.length; i++) {
    const diff = Math.abs(new Date(snapshots[i]!.timestamp).getTime() - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }
  return bestIdx;
}

function SliderView({
  left,
  right,
  colors,
}: {
  left: SnapshotHistoryItem;
  right: SnapshotHistoryItem;
  colors: { selected: string; comparison: string; overlay: string; overlayText: string };
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(0.5);
  const isDragging = useRef(false);

  const leftSrc = useSignedUrl(left.snapshot);
  const rightSrc = useSignedUrl(right.snapshot);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true;
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) setPosition(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)));
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) setPosition(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)));
    };
    const onUp = () => {
      isDragging.current = false;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  if (!rightSrc) {
    return (
      <div
        style={{
          width: "100%",
          aspectRatio: "4/3",
          borderRadius: 8,
          backgroundColor: colors.overlay,
        }}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        display: "inline-block",
        maxWidth: "100%",
        maxHeight: "100%",
        userSelect: "none",
        borderRadius: 8,
        overflow: "hidden",
        fontFamily: "Inter, sans-serif",
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Base image in normal flow — sizes the container */}
      <img src={rightSrc} style={{ display: "block", maxWidth: "100%", maxHeight: "100%", height: "auto" }} />
      {/* Comparison label */}
      <div
        style={{
          position: "absolute",
          bottom: 12,
          right: 12,
          background: colors.overlay,
          padding: "4px 10px",
          borderRadius: 4,
          fontSize: 12,
          fontWeight: 600,
          color: colors.overlayText,
          borderLeft: `3px solid ${colors.comparison}`,
        }}
      >
        {formatTimestamp(right.timestamp)} ({formatTimeDiff(left.timestamp, right.timestamp)})
      </div>
      {/* Left image overlay with clip */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          clipPath: `inset(0 ${((1 - position) * 100).toFixed(2)}% 0 0)`,
        }}
      >
        {leftSrc && <img src={leftSrc} style={{ display: "block", width: "100%", height: "100%" }} />}
        <div
          style={{
            position: "absolute",
            bottom: 12,
            left: 12,
            background: colors.overlay,
            padding: "4px 10px",
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 600,
            color: colors.overlayText,
            borderLeft: `3px solid ${colors.selected}`,
          }}
        >
          {formatTimestamp(left.timestamp)} (selected)
        </div>
      </div>
      {/* Slider handle */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: `${(position * 100).toFixed(2)}%`,
          height: "100%",
          width: 3,
          background: colors.selected,
          cursor: "ew-resize",
          transform: "translateX(-50%)",
          zIndex: 10,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 20,
            height: 36,
            background: colors.selected,
            borderRadius: 4,
            transform: "translate(-50%, -50%)",
          }}
        />
      </div>
    </div>
  );
}

interface SceneCompareContentProps {
  cameraKey: string;
  siteId: string;
}

export function SceneCompareContent({ cameraKey, siteId }: SceneCompareContentProps) {
  const router = useRouter();
  const { theme } = useUnistyles();
  const { user } = useAuth();
  const { getBooleanFlag } = useFeatureFlags();
  const isSupportUser = user?.roles?.includes("a3s:support") ?? false;
  const isEnabled = getBooleanFlag(FEATURE_FLAGS.SCENE_COMPARE);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [offsetDays, setOffsetDays] = useState(0);

  const now = useMemo(() => new Date(), []);
  const endTime = useMemo(
    () => toISODateString(new Date(now.getTime() - offsetDays * 24 * 60 * 60 * 1000)),
    [now, offsetDays],
  );
  const startTime = useMemo(
    () => toISODateString(new Date(now.getTime() - (offsetDays + 2) * 24 * 60 * 60 * 1000)),
    [now, offsetDays],
  );

  const parsedSiteId = useMemo(() => SiteId.fromString(siteId), [siteId]);
  const parsedCameraKey = useMemo(() => AssetCameraKey.fromString(cameraKey), [cameraKey]);

  const {
    data: snapshots,
    isLoading,
    isFetching,
  } = useSnapshotHistory({
    siteId: parsedSiteId,
    assetCameraKey: parsedCameraKey,
    startTime,
    endTime,
    enabled: isSupportUser && isEnabled,
  });

  const sortedSnapshots = useMemo(() => {
    if (!snapshots) return [];
    return [...snapshots].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [snapshots]);

  const clampedIndex = sortedSnapshots.length > 0 ? Math.min(selectedIndex, sortedSnapshots.length - 1) : 0;

  useEffect(() => {
    if (clampedIndex !== selectedIndex) setSelectedIndex(clampedIndex);
  }, [clampedIndex, selectedIndex]);

  const comparisonIndex = useMemo(() => {
    if (sortedSnapshots.length < 2) return 0;
    return findClosestTo24hBefore(sortedSnapshots, clampedIndex);
  }, [sortedSnapshots, clampedIndex]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Escape") router.back();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [router]);

  const compareColors = {
    selected: theme.colors.success[500] as string,
    comparison: theme.colors.red[600] as string,
    overlay: (theme.colors.mediaOverlay?.backgroundMedium ?? "rgba(0,0,0,0.5)") as string,
    overlayText: (theme.colors.mediaOverlay?.foreground ?? "#fff") as string,
  };

  if (!isSupportUser || !isEnabled) {
    return (
      <View flex={1} justifyContent="center" alignItems="center">
        <Text textStyle="md/regular" color="textMuted">
          Access restricted to support users.
        </Text>
      </View>
    );
  }

  const leftSnapshot = sortedSnapshots.length > 0 ? sortedSnapshots[clampedIndex] : undefined;
  const rightSnapshot = sortedSnapshots.length >= 2 ? sortedSnapshots[comparisonIndex] : undefined;

  return (
    <View flex={1} style={{ overflow: "hidden", fontFamily: "Inter, sans-serif" } as any}>
      <View flex={1} flexDirection="row" style={{ overflow: "hidden", minHeight: 0 }}>
        {/* Slider viewport */}
        <View
          flex={1}
          p="3xl"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", minHeight: 0 }}
        >
          {isLoading ?
            <Text textStyle="md/regular" color="textMuted">
              Loading snapshots...
            </Text>
          : !leftSnapshot || !rightSnapshot ?
            <Text textStyle="md/regular" color="textMuted">
              Not enough snapshots in this window.
            </Text>
          : <SliderView left={leftSnapshot} right={rightSnapshot} colors={compareColors} />}
        </View>

        {/* Sidebar */}
        <View
          width={260}
          borderLeftWidth={1}
          borderColor="borderCard"
          style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}
        >
          {/* Sidebar header */}
          <View flexDirection="row" alignItems="center" justifyContent="space-between" p="3xl">
            <Text textStyle="sm/semibold" color="text">
              Snapshots
            </Text>
            <Text textStyle="xs/regular" color="textMuted">
              {formatDateRange(startTime, endTime)}
            </Text>
          </View>

          {/* Snapshot list */}
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: 12, paddingBottom: 12 }}>
            {sortedSnapshots.map((snap, i) => {
              const isSelected = i === clampedIndex;
              const isComparison = i === comparisonIndex;
              return (
                <div
                  key={snap.timestamp}
                  onClick={() => setSelectedIndex(i)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 12,
                    fontFamily: "monospace",
                    marginBottom: 2,
                    background:
                      isSelected && isComparison ?
                        `linear-gradient(90deg, ${compareColors.selected}22, ${compareColors.comparison}22)`
                      : isSelected ? `${compareColors.selected}22`
                      : isComparison ? `${compareColors.comparison}22`
                      : "transparent",
                    border:
                      isSelected ? `1px solid ${compareColors.selected}`
                      : isComparison ? `1px solid ${compareColors.comparison}`
                      : "1px solid transparent",
                    color: theme.colors.text as string,
                  }}
                >
                  {formatTimestamp(snap.timestamp)}
                  {isSelected && " (selected)"}
                  {isComparison &&
                    !isSelected &&
                    leftSnapshot &&
                    ` (${formatTimeDiff(leftSnapshot.timestamp, snap.timestamp)})`}
                </div>
              );
            })}
          </ScrollView>

          {/* Navigation footer */}
          <View
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            p="3xl"
            borderTopWidth={1}
            borderColor="borderCard"
          >
            <button
              onClick={() => setOffsetDays((d) => d + 1)}
              disabled={isFetching}
              style={{
                background: "transparent",
                border: "none",
                color: theme.colors.interactive.buttons.default_background as string,
                cursor: isFetching ? "wait" : "pointer",
                fontSize: 13,
                fontWeight: 500,
                padding: 0,
              }}
            >
              ‹ Older
            </button>
            <Text textStyle="xs/regular" color="textMuted">
              {isFetching ? "Loading..." : `${String(sortedSnapshots.length)} snapshots`}
            </Text>
            <button
              onClick={() => setOffsetDays((d) => Math.max(0, d - 1))}
              disabled={offsetDays === 0 || isFetching}
              style={{
                background: "transparent",
                border: "none",
                color: theme.colors.interactive.buttons.default_background as string,
                cursor: offsetDays === 0 || isFetching ? "not-allowed" : "pointer",
                fontSize: 13,
                fontWeight: 500,
                padding: 0,
                opacity: offsetDays === 0 ? 0.4 : 1,
              }}
            >
              Newer ›
            </button>
          </View>
        </View>
      </View>
    </View>
  );
}
